































































































































































































































































import { AppointmentApi } from '@/api/appointments';
import { ClientAccountsApi } from '@/api/client-accounts';
import { CreateAppointmentRequest } from '@/api/requests/appointments/create-appointment-request';
import { UpdateAppointmentRequest } from '@/api/requests/appointments/update-appointment-request';
import addressWithLookup from '@/components/address-with-lookup.vue';
import loading from '@/components/loading.vue';
import { Appointment, createAppointmentBooking } from '@/shared/appointments/appointment';
import { ClientAccount } from '@/shared/clients/client-account';
import { ClientAccountAddress, createClientAccountAddress } from '@/shared/clients/client-account-address';
import { LocationType } from '@/shared/location-type';
import { PartnerPractice } from '@/shared/partner-practice';
import { PartnerPracticeAddress } from '@/shared/partner-practice-address';
import { AppointmentReasonType } from '@/shared/types/appointment-reason-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { User } from '@/shared/user';
import { UserStatus } from '@/shared/user-status';
import { formatDate } from '@/shared/utilities/date-utilities';
import { DateTime } from 'luxon';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const typesModule = namespace('types');
const userModule = namespace('user');
const usersModule = namespace('users');

const appointmentApi = new AppointmentApi();
const clientApi = new ClientAccountsApi();

@Component({
    components: {
        addressWithLookup,
        loading,
    },
})
export default class BookAppointmentDialog extends Vue {
    @partnerPracticesModule.Getter('partnerPracticesForCurrentUser') private partnerPractices!: Array<PartnerPractice>;
    @partnerPracticesModule.Getter('partnerPracticeName') private partnerPracticeName!: (partnerPracticeId: string | null) => string;
    @partnerPracticesModule.Getter('partnerPracticeAddresses') private addresses!: (partnerPracticeId: string | null) => Array<PartnerPracticeAddress>;

    @typesModule.Getter('activeAppointmentTypes') private appointmentTypes!: Array<AppointmentType>;
    @typesModule.Getter('appointmentReasonTypes') private appointmentReasonTypes!: Array<AppointmentReasonType>;

    @userModule.Getter('user') private currentUser!: User;
    @userModule.Getter('isInternalKeyStakeholder') private isInternalKeyStakeholder!: boolean;

    @usersModule.Getter('rightLegalGroupUsers') private rightLegalGroupUsers!: (status: UserStatus) => Array<User>;
    @usersModule.Getter('activePartnerPracticeUsers') private partnerPracticeUsers!: (partnerPracticeId: string) => Array<User>;
    @usersModule.Getter('activeLegalAdvisors') private legalAdvisors!: (partnerPracticeId: string) => Array<User>;

    @Prop({ required: true }) private visible!: boolean;
    @Prop({ required: true }) private client!: ClientAccount;
    @Prop({ required: false, default: null }) private originalAppointment!: Appointment;
    @Prop({ required: false, default: false }) private isRebook!: boolean;
    @Prop({ required: false, default: false }) private isEdit!: boolean;
    @Prop({ required: false, default: false }) private isSigning!: boolean;
    @Prop({ required: false, default: null }) private interactionId!: string | null;
    @Prop({ required: false, default: null }) private clientId!: string | null;
    @Prop({ required: false, default: null }) private appointmentDate!: string | Date | null;

    private isVisible: boolean = this.visible;
    private isLoading: boolean = true;
    private isSaving: boolean = false;
    private detailsCompleted: boolean = false;
    private locationCompleted: boolean = false;
    private reasonsCompleted: boolean = false;
    private notesCompleted: boolean = false;
    private currentStep: number = 0;
    private appointment: Appointment = this.originalAppointment == null ? createAppointmentBooking({ ...this.client }) : { ...this.originalAppointment };
    private selectedBookerId: string | null = null;
    private selectedClientId: string = this.clientId != null
        ? this.clientId
        : this.originalAppointment != null && this.originalAppointment.clients.length > 1
            ? 'both'
            : this.client!.id!;
    private selectedClient: ClientAccount = { ...this.client };
    private selectedReasonIds: Array<string> = new Array<string>();
    private useClientAddress: boolean = true;
    private alternativeClientAddress: ClientAccountAddress = createClientAccountAddress();
    private selectedDate: string | Date | null = null;
    private selectedHour: number | null = null;
    private selectedMinute: number | null = null;
    private selectedMeridian: string = 'AM';
    private useTwentyFourHours: boolean = false;
    private assignPartnerConfirmed: boolean = false;

    private get title(): string {
        if (this.isRebook) {
            return 'Re-book Appointment';
        }

        if (this.isEdit) {
            return 'Edit Appointment';
        }

        return 'Book Appointment';
    }

    private get showLocationStep(): boolean {
        if (this.appointment.appointmentTypeId == null) {
            return false;
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === this.appointment.appointmentTypeId);
        return appointmentType == null ? false : appointmentType.isLocationRequired;
    }

    private get showReasonsStep(): boolean {
        if (this.appointment.appointmentTypeId == null) {
            return false;
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === this.appointment.appointmentTypeId);
        return appointmentType == null ? false : appointmentType.isInitialAppointment;
    }

    private get maxSteps(): number {
        let steps: number = 2;

        if (this.showLocationStep) {
            steps++;
        }

        if (this.showReasonsStep) {
            steps++;
        }

        return steps;
    }

    private get canEditClient(): boolean {
        return this.isEdit ? this.appointment != null && this.appointment.outcome == null : true;
    }

    private get canEditAppointmentType(): boolean {
        return this.isEdit ? this.appointment != null && this.appointment.outcome == null : !this.isSigning;
    }

    private get canEditPartnerPractice(): boolean {
        if (this.partnerPractices.length === 1) { return false; }

        return this.isEdit ? this.appointment != null && this.appointment.outcome == null : true;
    }

    private get canEditAppointmentDate(): boolean {
        return this.isEdit ? this.isInternalKeyStakeholder : true;
    }

    private get hours(): Array<{ value: number, description: string }> {
        const result: Array<{ value: number, description: string }> = [];

        for (let i = 0; i < (this.useTwentyFourHours ? 24 : 12); i++) {
            let value: number = i;
            let description: string = value.toString();

            if (!this.useTwentyFourHours) {
                value = i + 1;
                description = value.toString();

                if (this.selectedMeridian === 'AM') {
                    if (value === 12) {
                        value = 0;
                    }
                } else {
                    if (value !== 12) {
                        value += 12;
                    }
                }
            }

            result.push({ value, description: description.padStart(2, '0') });
        }

        return result;
    }

    private get minutes(): Array<{ value: number, description: string }> {
        return [
            { value: 0, description: '00' },
            { value: 15, description: '15' },
            { value: 30, description: '30' },
            { value: 45, description: '45' },
        ];
    }

    private get bookers(): Array<User> {
        const users = [...this.rightLegalGroupUsers(UserStatus.Active)];

        if (this.client.partnerPracticeId != null && this.client.partnerPracticeId !== '00000000-0000-0000-0000-000000000000') {
            for (const user of this.partnerPracticeUsers(this.client.partnerPracticeId!)) {
                users.push({ ...user });
            }
        }

        return users.sort((a, b) => {
            const aFullName = `${a.firstName.trim()} ${a.lastName.trim()}`.trim();
            const bFullName = `${b.firstName.trim()} ${b.lastName.trim()}`.trim();

            if (aFullName > bFullName) { return 1; }
            if (aFullName < bFullName) { return -1; }
            return 0;
        });
    }

    private get partnerPracticesForAcquisition(): Array<PartnerPractice> {
        return this.partnerPractices.filter((pp) => pp.originalSourceTypes.some((id) => id === this.client.originalSourceId));
    }

    private get locationTypes(): Array<{ id: number, value: string }> {
        return [
            { id: LocationType.Office, value: 'Office' },
            { id: LocationType.Home, value: 'Home' },
            { id: LocationType.Telephone, value: 'Telephone' },
            { id: LocationType.Video, value: 'Video' },
        ];
    }

    private get reasonTypes(): Array<AppointmentReasonType> {
        if (this.appointmentReasonTypes == null || !this.appointmentReasonTypes.length) {
            return [];
        }

        return this.appointmentReasonTypes.filter((type) => !type.isArchived || this.selectedReasonIds.some((id) => id === type.id));
    }

    private get formatClientAndPartnerNames(): string {
        const clientFirstName = this.client.firstName != null ? this.client.firstName.trim() : '';
        const clientLastName = this.client.lastName != null ? this.client.lastName.trim() : '';

        if (this.client.partner == null) {
            return `${clientFirstName} ${clientLastName}`.trim();
        }

        const partnerFirstName = this.client.partner.firstName != null ? this.client.partner.firstName.trim() : '';
        const partnerLastName = this.client.partner.lastName != null ? this.client.partner.lastName.trim() : '';

        if (clientLastName.toLowerCase() === partnerLastName.toLowerCase()) {
            return `${clientFirstName} and ${partnerFirstName} ${clientLastName}`.trim();
        }

        return `${clientFirstName} ${clientLastName} and ${partnerFirstName} ${partnerLastName}`.trim();
    }

    private get clientDescription(): string {
        if (!this.appointment.clients.length) {
            return '';
        }

        const clientFirstName = this.appointment.clients[0].firstName != null ? this.appointment.clients[0].firstName.trim() : '';
        const clientLastName = this.appointment.clients[0].lastName != null ? this.appointment.clients[0].lastName.trim() : '';

        if (this.appointment.clients.length < 2) {
            return `${clientFirstName} ${clientLastName}`.trim();
        }

        const partnerFirstName = this.appointment.clients[1].firstName != null ? this.appointment.clients[1].firstName.trim() : '';
        const partnerLastName = this.appointment.clients[1].lastName != null ? this.appointment.clients[1].lastName.trim() : '';

        if (clientLastName.toLowerCase() === partnerLastName.toLowerCase()) {
            return `${clientFirstName} and ${partnerFirstName} ${clientLastName}`.trim();
        }

        return `${clientFirstName} ${clientLastName} and ${partnerFirstName} ${partnerLastName}`.trim();
    }

    private get appointmentTypeDescription(): string {
        if (this.appointment.appointmentTypeId == null) {
            return '';
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === this.appointment.appointmentTypeId);
        return appointmentType == null ? '' : appointmentType.value;
    }

    private get invalidClientAddress(): boolean {
        return this.client!.addressLine1 == null
            || this.client!.addressLine1.toUpperCase() === 'UNKNOWN'
            || this.client!.postcode == null
            || this.client!.postcode.toUpperCase() === 'UNKNOWN';
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.$validator.errors.clear();
        this.isVisible = visible;
    }

    @Watch('selectedClientId')
    private async onSelectedClientIdChanged(): Promise<void> {
        if (this.selectedClientId === 'both' || this.selectedClientId === this.client.id!) {
            this.selectedClient = { ...this.client };
        } else {
            this.selectedClient = await clientApi.getClientAccountByIdAsync(this.selectedClientId);
        }
    }

    @Watch('appointment.partnerPracticeId')
    private async onAppointmentPartnerPracticeIdChanged(): Promise<void> {
        this.appointment.legalAdvisorId = null;
        this.appointment.locationType = null;
    }

    @Watch('useTwentyFourHours')
    private onUseTwentyFourHoursChanged(): void {
        if (this.selectedHour == null) {
            return;
        }

        this.selectedMeridian = this.selectedHour >= 12 ? 'PM' : 'AM';
    }

    @Watch('appointment.locationType')
    private onLocationTypeChanged(locationType: LocationType): void {
        if (locationType !== LocationType.Office) {
            this.appointment.partnerPracticeAddressId = null;
        }

        if (locationType === LocationType.Home && this.invalidClientAddress) {
            this.useClientAddress = false;
        }
    }

    private async onSave(): Promise<void> {
        const isValid = await this.$validator.validateAll();

        if (!isValid) {
            return;
        }

        this.isSaving = true;

        try {
            const clients: Array<string> = new Array<string>();

            if (this.selectedClientId === 'both') {
                clients.push(this.client.id!);
                clients.push(this.client.partner!.id!);
            } else {
                clients.push(this.selectedClientId!);
            }

            this.appointment.date = DateTime.fromJSDate(new Date(this.selectedDate!))
                .set({ hour: this.selectedHour!, minute: this.selectedMinute! })
                .toUTC()
                .toJSDate();

            if (this.appointment.locationType === LocationType.Home) {
                this.appointment.addressLine1 = this.useClientAddress ? this.client.addressLine1 : this.alternativeClientAddress!.addressLine1;
                this.appointment.addressLine2 = this.useClientAddress ? this.client.addressLine2 : this.alternativeClientAddress!.addressLine2;
                this.appointment.addressLine3 = this.useClientAddress ? this.client.addressLine3 : this.alternativeClientAddress!.addressLine3;
                this.appointment.town = this.useClientAddress ? this.client.town : this.alternativeClientAddress!.town;
                this.appointment.county = this.useClientAddress ? this.client.county : this.alternativeClientAddress!.county;
                this.appointment.postcode = this.useClientAddress ? this.client.postcode : this.alternativeClientAddress!.postcode;
            } else {
                this.appointment.addressLine1 = null;
                this.appointment.addressLine2 = null;
                this.appointment.addressLine3 = null;
                this.appointment.town = null;
                this.appointment.county = null;
                this.appointment.postcode = null;
            }

            const appointmentType = this.appointmentTypes.find((type) => type.id === this.appointment.appointmentTypeId);

            if (this.isEdit) {
                const updateRequest: UpdateAppointmentRequest = {
                    ...this.appointment,
                    clients,
                    appointmentTypeId: this.appointment.appointmentTypeId!,
                    partnerPracticeId: this.appointment.partnerPracticeId!,
                    legalAdvisorId: this.appointment.legalAdvisorId!,
                    bookerId: this.selectedBookerId != null ? this.selectedBookerId : this.currentUser.id!,
                    date: this.appointment.date!,
                    locationType: this.appointment.locationType!,
                    reasons: appointmentType!.isInitialAppointment ? this.selectedReasonIds : [],
                    version: this.appointment.version!,
                };

                await appointmentApi.updateAppointmentAsync(this.appointment.id!, updateRequest);

                this.$emit('ok', { ...updateRequest });
            } else {
                const createRequest: CreateAppointmentRequest = {
                    ...this.appointment,
                    clients,
                    appointmentTypeId: this.appointment.appointmentTypeId!,
                    partnerPracticeId: this.appointment.partnerPracticeId!,
                    legalAdvisorId: this.appointment.legalAdvisorId!,
                    bookerId: this.selectedBookerId != null ? this.selectedBookerId : this.currentUser.id!,
                    interactionId: this.interactionId,
                    date: this.appointment.date!,
                    locationType: this.appointment.locationType!,
                    reasons: appointmentType!.isInitialAppointment ? this.selectedReasonIds : [],
                };

                if (this.isRebook) {
                    await appointmentApi.reBookAppointmentAsync(this.originalAppointment.id!, createRequest);
                } else {
                    await appointmentApi.createAppointmentAsync(createRequest);
                }

                this.$emit('ok', { ...createRequest });
            }
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not book appointment, please try again.', type: 'is-danger' });
            }
        }

        this.isSaving = false;
    }

    private async onNext(): Promise<void> {
        let currentStep = this.currentStep;

        if (this.currentStep > 0 && !this.showLocationStep) {
            currentStep++;
        }

        if (this.currentStep > 1 && !this.showReasonsStep) {
            currentStep++;
        }

        const isValid = await this.$validator.validateAll();

        if (!isValid) {
            return;
        }

        switch (currentStep) {
            case 0: // Details
                if (this.client.isAcquisition && this.client.partnerPracticeId !== this.appointment.partnerPracticeId && this.client.partner != null && this.selectedClientId !== 'both' && !this.assignPartnerConfirmed) {
                    await this.$confirm.open({
                        message: `The client you are booking an appointment for has a partner, if you continue the partner will also be assigned to ${this.partnerPracticeName(this.appointment.partnerPracticeId!)}.`,
                        showCancel: true,
                        okText: 'Continue',
                        cancelText: 'Cancel',
                        onOk: () => {
                            this.assignPartnerConfirmed = true;
                            this.onNext();
                        },
                        onCancel: () => {
                            this.assignPartnerConfirmed = false;
                            return;
                        },
                    });
                }

                this.assignPartnerConfirmed = false;

                this.detailsCompleted = true;

                if (!this.showLocationStep) {
                    this.locationCompleted = true;
                }

                if (!this.showReasonsStep) {
                    this.reasonsCompleted = true;
                }

                break;

            case 1: // Location
                if (this.appointment.locationType === LocationType.Home && !this.useClientAddress) {
                    const addressIsValid = await (this.$refs.address as Vue).$validator.validateAll('address-details');

                    if (!addressIsValid) {
                        return;
                    }
                }

                this.locationCompleted = true;

                break;

            case 2: // Reasons
                this.reasonsCompleted = true;
                break;

            case 3: // Notes
                this.notesCompleted = true;
                break;
        }

        setTimeout(() => {
            this.currentStep++;
        }, 0);
    }

    private onPrevious(): void {
        this.currentStep--;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }

    private onMeridianChanged(value: string | null): void {
        if (this.selectedHour == null) {
            return;
        }

        if (value === 'AM') {
            this.selectedHour = this.selectedHour === 12 ? 0 : this.selectedHour - 12;
        } else {
            this.selectedHour = this.selectedHour === 0 ? 12 : this.selectedHour + 12;
        }
    }

    private populateExistingAddress(): void {
        if (this.appointment.locationType !== LocationType.Home) {
            return;
        }

        if (this.appointment.addressLine1 === this.client.addressLine1 &&
            this.appointment.addressLine2 === this.client.addressLine2 &&
            this.appointment.addressLine3 === this.client.addressLine3 &&
            this.appointment.town === this.client.town &&
            this.appointment.county === this.client.county &&
            this.appointment.postcode === this.client.postcode) {
            this.useClientAddress = true;
            return;
        }

        this.useClientAddress = false;
        this.alternativeClientAddress.addressLine1 = this.appointment.addressLine1;
        this.alternativeClientAddress.addressLine2 = this.appointment.addressLine2;
        this.alternativeClientAddress.addressLine3 = this.appointment.addressLine3;
        this.alternativeClientAddress.town = this.appointment.town;
        this.alternativeClientAddress.county = this.appointment.county;
        this.alternativeClientAddress.postcode = this.appointment.postcode;
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`users/loadAllUsersAsync`),
        ]);

        if (this.client.isAcquisition && this.partnerPractices.length === 1) {
            this.appointment.partnerPracticeId = this.partnerPractices[0].id!;
        }

        if (this.originalAppointment == null) {
            this.selectedBookerId = this.isInternalKeyStakeholder ? null : this.currentUser.id!;
        } else {
            this.selectedBookerId = this.originalAppointment.booker!.id!;

            if (this.isRebook && !this.bookers.some((user) => user.id! === this.originalAppointment.booker!.id!)) {
                this.selectedBookerId = this.isInternalKeyStakeholder ? null : this.currentUser.id!;
            }
        }

        if (this.isSigning) {
            this.appointment.appointmentTypeId = this.appointmentTypes.find((type) => type.isSigningAppointment)!.id!;
        }

        if (this.appointmentDate != null) {
            this.selectedDate = formatDate(this.appointmentDate);
        } else if (this.originalAppointment != null && this.originalAppointment.date != null) {
            const appointmentDate = formatDate(this.originalAppointment.date);

            this.selectedDate = appointmentDate;
            this.selectedMeridian = appointmentDate!.getHours() >= 12 ? 'PM' : 'AM';
            this.selectedHour = appointmentDate!.getHours();
            this.selectedMinute = appointmentDate!.getMinutes();
        }

        if (this.originalAppointment != null) {
            this.populateExistingAddress();
        }

        if (this.appointment.reasons != null && this.appointment.reasons.length) {
            this.selectedReasonIds = this.appointment.reasons.map((reason) => reason.reasonTypeId!);
        }

        this.isLoading = false;
    }
}
